// Configuration
const DB_NAME = 'cotelub_offline_files';
const DB_VERSION = 1;
const STORE_FILES = 'files';
const STORE_METADATA = 'metadata';
const INDEX_FILE_ID = 'fileId';

// Limites
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 2000MB par fichier
const MAX_STORAGE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB total
const DOWNLOAD_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const CLEANUP_AGE = 30 * 24 * 60 * 60 * 1000; // 30 jours
const STORAGE_WARNING_THRESHOLD = 0.1; // 10% d'espace restant

interface FileMetadata {
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  downloadDate: number;
  lastAccessed: number;
  sourceUrl: string;
  version?: number;
}

interface StorageInfo {
  usage: number;
  quota: number;
  available: number;
  percentage: number;
}

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<IDBDatabase> | null = null;

  /**
   * Initialise la base de données IndexedDB
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error("Erreur lors de l'ouverture de la base de données"));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store pour les fichiers (Blob)
        if (!db.objectStoreNames.contains(STORE_FILES)) {
          const fileStore = db.createObjectStore(STORE_FILES, {
            keyPath: 'fileId'
          });
          fileStore.createIndex(INDEX_FILE_ID, 'fileId', { unique: true });
        }

        // Store pour les métadonnées
        if (!db.objectStoreNames.contains(STORE_METADATA)) {
          const metadataStore = db.createObjectStore(STORE_METADATA, {
            keyPath: 'fileId'
          });
          metadataStore.createIndex('downloadDate', 'downloadDate', {
            unique: false
          });
          metadataStore.createIndex('lastAccessed', 'lastAccessed', {
            unique: false
          });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Obtient la base de données (l'initialise si nécessaire)
   */
  private async getDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB();
    }
    return this.db!;
  }

  /**
   * Sauvegarde un fichier dans IndexedDB
   */
  async saveFile(
    fileId: string,
    blob: Blob,
    metadata: Omit<FileMetadata, 'fileId' | 'downloadDate' | 'lastAccessed'>
  ): Promise<void> {
    const db = await this.getDB();

    // Valider le Blob avant traitement
    if (!blob || !(blob instanceof Blob)) {
      throw new Error('Blob invalide fourni pour la sauvegarde');
    }

    const blobSize = blob.size;
    const blobType = blob.type;

    // Logs pour diagnostic
    console.log(
      `[saveFile] Début sauvegarde - fileId: ${fileId}, taille: ${blobSize} bytes (${(blobSize / 1024 / 1024).toFixed(2)} MB), type: ${blobType}`
    );

    const downloadDate = Date.now();
    const fileMetadata: FileMetadata = {
      fileId,
      downloadDate,
      lastAccessed: downloadDate,
      ...metadata,
      // Mettre à jour avec la taille réelle du Blob
      fileSize: blobSize,
      mimeType: blobType || metadata.mimeType
    };

    // Sauvegarder d'abord les métadonnées dans une transaction séparée
    // Cela évite les problèmes de limite de taille par transaction
    try {
      await this._saveMetadata(db, fileMetadata);
      console.log(`[saveFile] Métadonnées sauvegardées pour ${fileId}`);
    } catch (error: any) {
      console.error('[saveFile] Erreur sauvegarde métadonnées:', error);
      throw new Error(
        `Erreur lors de la sauvegarde des métadonnées: ${error.message}`
      );
    }

    // Puis sauvegarder le fichier dans une transaction séparée
    try {
      await this._saveFileBlob(db, fileId, blob);
      console.log(`[saveFile] Fichier sauvegardé avec succès: ${fileId}`);
    } catch (error: any) {
      console.error('[saveFile] Erreur sauvegarde fichier:', error);
      // Essayer de nettoyer les métadonnées en cas d'échec
      try {
        await this._deleteMetadata(db, fileId);
      } catch (cleanupError) {
        console.error('[saveFile] Erreur nettoyage métadonnées:', cleanupError);
      }
      throw error;
    }
  }

  /**
   * Sauvegarde uniquement les métadonnées
   */
  private _saveMetadata(
    db: IDBDatabase,
    metadata: FileMetadata
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readwrite');

      transaction.onerror = (event) => {
        const error = transaction.error || (event.target as IDBRequest)?.error;
        const errorMessage = error
          ? `Erreur sauvegarde métadonnées: ${error.name} (Code: ${error.code}) - ${error.message}`
          : 'Erreur sauvegarde métadonnées: Erreur inconnue';
        console.error('[saveMetadata] Transaction error:', error);
        reject(new Error(errorMessage));
      };

      transaction.onabort = () => {
        console.error('[saveMetadata] Transaction aborted');
        reject(
          new Error('Transaction annulée lors de la sauvegarde des métadonnées')
        );
      };

      transaction.oncomplete = () => {
        resolve();
      };

      const metadataStore = transaction.objectStore(STORE_METADATA);
      const putRequest = metadataStore.put(metadata);

      putRequest.onerror = (event) => {
        const error = (event.target as IDBRequest)?.error;
        const errorMessage = error
          ? `Erreur put métadonnées: ${error.name} (Code: ${error.code}) - ${error.message}`
          : "Erreur lors de l'opération put sur les métadonnées";
        console.error(
          '[saveMetadata] Put error:',
          error,
          'metadata:',
          metadata
        );
        reject(new Error(errorMessage));
      };
    });
  }

  /**
   * Sauvegarde uniquement le Blob du fichier
   */
  private _saveFileBlob(
    db: IDBDatabase,
    fileId: string,
    blob: Blob
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // Valider le Blob une dernière fois
      if (!blob || blob.size === 0) {
        reject(new Error(`Blob invalide ou vide pour ${fileId}`));
        return;
      }

      // Vérifier l'état du stockage avant de continuer
      try {
        const storageInfo = await this._getStorageInfoDetailed();
        console.log('[saveFileBlob] État du stockage:', storageInfo);

        // Vérifier si on a suffisamment d'espace (avec une marge de sécurité de 20%)
        const requiredSpace = blob.size * 1.2; // 20% de marge
        if (storageInfo.available < requiredSpace) {
          const errorMsg = `Espace insuffisant. Disponible: ${this._formatBytes(storageInfo.available)}, Requis: ${this._formatBytes(requiredSpace)} (${this._formatBytes(blob.size)} + 20% marge)`;
          console.error('[saveFileBlob]', errorMsg);
          reject(new Error(errorMsg));
          return;
        }

        // Vérifier le quota
        if (storageInfo.usage + blob.size > storageInfo.quota) {
          const errorMsg = `Quota dépassé. Utilisé: ${this._formatBytes(storageInfo.usage)}, Quota: ${this._formatBytes(storageInfo.quota)}, Tentative: ${this._formatBytes(blob.size)}`;
          console.error('[saveFileBlob]', errorMsg);
          reject(new Error(errorMsg));
          return;
        }
      } catch (storageError: any) {
        console.warn(
          '[saveFileBlob] Impossible de vérifier le stockage:',
          storageError
        );
        // Continuer quand même, mais logger l'avertissement
      }

      // Vérifier que le Blob peut être lu (s'assurer qu'il n'est pas "consumed")
      let validatedBlob: Blob = blob;
      try {
        const testSlice = blob.slice(0, Math.min(1, blob.size));
        if (!testSlice || testSlice.size === 0) {
          throw new Error('Blob ne peut pas être lu');
        }
        console.log('[saveFileBlob] Blob valide et lisible');

        // Pour certains navigateurs/mobiles, reconstruire le Blob peut résoudre les problèmes de sérialisation
        // Essayons de reconstruire le Blob en le convertissant d'abord en ArrayBuffer
        try {
          const arrayBuffer = await blob.arrayBuffer();
          validatedBlob = new Blob([arrayBuffer], { type: blob.type });
          console.log(
            '[saveFileBlob] Blob reconstruit avec succès (taille originale:',
            blob.size,
            ', nouvelle:',
            validatedBlob.size,
            ')'
          );

          // Vérifier que la taille correspond
          if (validatedBlob.size !== blob.size) {
            console.warn(
              '[saveFileBlob] Attention: la taille du Blob reconstruit diffère',
              {
                original: blob.size,
                rebuilt: validatedBlob.size
              }
            );
            // Utiliser le Blob original si les tailles diffèrent (mieux vaut prévenir que guérir)
            validatedBlob = blob;
          }
        } catch (reconstructionError: any) {
          console.warn(
            '[saveFileBlob] Impossible de reconstruire le Blob, utilisation du Blob original:',
            reconstructionError.message
          );
          // Utiliser le Blob original en cas d'échec
          validatedBlob = blob;
        }
      } catch (blobError: any) {
        const errorMsg = `Blob invalide ou corrompu: ${blobError.message}`;
        console.error('[saveFileBlob]', errorMsg);
        reject(new Error(errorMsg));
        return;
      }

      const transaction = db.transaction([STORE_FILES], 'readwrite');

      transaction.onerror = (event) => {
        const error = transaction.error || (event.target as IDBRequest)?.error;

        // Obtenir plus d'informations sur l'erreur
        let errorDetails = '';
        if (error) {
          errorDetails = `${error.name} (Code: ${error.code}) - ${error.message}`;
        }

        const errorMessage = `Erreur sauvegarde fichier: ${errorDetails} - Taille blob: ${validatedBlob.size} bytes (${(validatedBlob.size / 1024 / 1024).toFixed(2)} MB)`;
        console.error('[saveFileBlob] Transaction error:', error, {
          fileId,
          blobSize: validatedBlob.size,
          blobSizeOriginal: blob.size,
          blobType: validatedBlob.type,
          blobReconstructed: validatedBlob !== blob,
          errorObject: error
        });
        reject(new Error(errorMessage));
      };

      transaction.onabort = () => {
        console.error('[saveFileBlob] Transaction aborted', {
          fileId,
          blobSize: validatedBlob.size,
          blobSizeOriginal: blob.size
        });
        reject(
          new Error(
            `Transaction annulée lors de la sauvegarde du fichier ${fileId} (${validatedBlob.size} bytes)`
          )
        );
      };

      transaction.oncomplete = () => {
        console.log(
          `[saveFileBlob] Transaction complétée avec succès - fileId: ${fileId}`
        );
        resolve();
      };

      const fileStore = transaction.objectStore(STORE_FILES);

      // Préparer l'objet à stocker avec le Blob validé/reconstruit
      const fileData = { fileId, blob: validatedBlob };

      console.log(
        `[saveFileBlob] Tentative de stockage - fileId: ${fileId}, objet size: ~${validatedBlob.size} bytes (${(validatedBlob.size / 1024 / 1024).toFixed(2)} MB)`
      );

      const putRequest = fileStore.put(fileData);

      putRequest.onerror = (event) => {
        const error = (event.target as IDBRequest)?.error;
        const errorMessage = error
          ? `Erreur lors de la sauvegarde du fichier (store files): ${error.name} (Code: ${error.code}) - ${error.message} - Taille: ${validatedBlob.size} bytes (${(validatedBlob.size / 1024 / 1024).toFixed(2)} MB)`
          : `Erreur lors de la sauvegarde du fichier dans le store files - Taille: ${validatedBlob.size} bytes`;

        // Logs détaillés pour diagnostic
        console.error('[saveFileBlob] Put error détails:', {
          error,
          errorName: error?.name,
          errorCode: error?.code,
          errorMessage: error?.message,
          fileId,
          blobSize: validatedBlob.size,
          blobSizeOriginal: blob.size,
          blobType: validatedBlob.type,
          blobReconstructed: validatedBlob !== blob,
          storageInfo: 'Vérification en cours...'
        });

        // Essayer d'obtenir l'état du stockage pour l'erreur
        this._getStorageInfoDetailed()
          .then((storageInfo) => {
            console.error(
              "[saveFileBlob] État du stockage au moment de l'erreur:",
              storageInfo
            );
          })
          .catch(() => {
            // Ignorer les erreurs de vérification
          });

        reject(new Error(errorMessage));
      };

      putRequest.onsuccess = () => {
        console.log(
          `[saveFileBlob] Fichier stocké avec succès - fileId: ${fileId}`
        );
      };
    });
  }

  /**
   * Obtient les informations de stockage détaillées (méthode privée dans IndexedDBService)
   */
  private async _getStorageInfoDetailed(): Promise<{
    usage: number;
    quota: number;
    available: number;
    percentage: number;
  }> {
    if (!navigator.storage || !navigator.storage.estimate) {
      return {
        usage: 0,
        quota: Infinity,
        available: Infinity,
        percentage: 0
      };
    }

    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 0;
    const available = quota - usage;
    const percentage = quota > 0 ? usage / quota : 0;

    return {
      usage,
      quota,
      available,
      percentage
    };
  }

  /**
   * Formate les bytes (méthode privée)
   */
  private _formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    if (bytes === Infinity) return 'Infinity';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Supprime uniquement les métadonnées (pour nettoyage en cas d'échec)
   */
  private _deleteMetadata(db: IDBDatabase, fileId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readwrite');
      const metadataStore = transaction.objectStore(STORE_METADATA);
      const deleteRequest = metadataStore.delete(fileId);

      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => {
        reject(new Error('Erreur lors du nettoyage des métadonnées'));
      };
    });
  }

  /**
   * Récupère un fichier depuis IndexedDB
   */
  async getFile(fileId: string): Promise<Blob | null> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_FILES, STORE_METADATA],
        'readonly'
      );
      const fileStore = transaction.objectStore(STORE_FILES);

      const request = fileStore.get(fileId);

      request.onerror = () => {
        reject(new Error('Erreur lors de la récupération du fichier'));
      };

      request.onsuccess = () => {
        const result = request.result;
        if (result && result.blob) {
          // Mettre à jour la date d'accès
          this.updateLastAccessed(fileId);
          resolve(result.blob);
        } else {
          resolve(null);
        }
      };
    });
  }

  /**
   * Met à jour la date d'accès d'un fichier
   */
  private async updateLastAccessed(fileId: string): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readwrite');
      const metadataStore = transaction.objectStore(STORE_METADATA);

      const getRequest = metadataStore.get(fileId);

      getRequest.onsuccess = () => {
        const metadata = getRequest.result;
        if (metadata) {
          metadata.lastAccessed = Date.now();
          const putRequest = metadataStore.put(metadata);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () =>
            reject(new Error('Erreur lors de la mise à jour'));
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => {
        reject(new Error('Erreur lors de la récupération des métadonnées'));
      };
    });
  }

  /**
   * Vérifie si un fichier est téléchargé
   */
  async isFileDownloaded(fileId: string): Promise<boolean> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readonly');
      const metadataStore = transaction.objectStore(STORE_METADATA);

      const request = metadataStore.get(fileId);

      request.onerror = () => {
        reject(new Error('Erreur lors de la vérification'));
      };

      request.onsuccess = () => {
        resolve(!!request.result);
      };
    });
  }

  /**
   * Récupère les métadonnées d'un fichier
   */
  async getMetadata(fileId: string): Promise<FileMetadata | null> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readonly');
      const metadataStore = transaction.objectStore(STORE_METADATA);

      const request = metadataStore.get(fileId);

      request.onerror = () => {
        reject(new Error('Erreur lors de la récupération des métadonnées'));
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });
  }

  /**
   * Supprime un fichier de IndexedDB
   */
  async deleteFile(fileId: string): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_FILES, STORE_METADATA],
        'readwrite'
      );

      transaction.onerror = (event) => {
        const error = transaction.error || (event.target as IDBRequest)?.error;
        const errorMessage = error
          ? `Erreur lors de la suppression du fichier: ${error.name} (Code: ${error.code}) - ${error.message}`
          : 'Erreur lors de la suppression: Erreur inconnue';
        console.error('IndexedDB delete error:', error);
        reject(new Error(errorMessage));
      };

      transaction.onabort = () => {
        const errorMessage = `Transaction annulée lors de la suppression du fichier ${fileId}`;
        console.error('IndexedDB transaction aborted during delete');
        reject(new Error(errorMessage));
      };

      transaction.oncomplete = () => {
        resolve();
      };

      const fileStore = transaction.objectStore(STORE_FILES);
      fileStore.delete(fileId);

      const metadataStore = transaction.objectStore(STORE_METADATA);
      metadataStore.delete(fileId);
    });
  }

  /**
   * Récupère tous les fichiers téléchargés
   */
  async getAllFiles(): Promise<FileMetadata[]> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readonly');
      const metadataStore = transaction.objectStore(STORE_METADATA);

      const request = metadataStore.getAll();

      request.onerror = () => {
        reject(new Error('Erreur lors de la récupération des fichiers'));
      };

      request.onsuccess = () => {
        resolve(request.result || []);
      };
    });
  }

  /**
   * Nettoie les fichiers anciens (> 30 jours sans accès)
   */
  async cleanOldFiles(): Promise<number> {
    const db = await this.getDB();
    const now = Date.now();
    const cutoffDate = now - CLEANUP_AGE;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_FILES, STORE_METADATA],
        'readwrite'
      );
      const metadataStore = transaction.objectStore(STORE_METADATA);
      const index = metadataStore.index('lastAccessed');

      const request = index.openCursor(IDBKeyRange.upperBound(cutoffDate));
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const metadata = cursor.value;
          const fileStore = transaction.objectStore(STORE_FILES);
          fileStore.delete(metadata.fileId);
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };

      request.onerror = () => {
        reject(new Error('Erreur lors du nettoyage'));
      };
    });
  }

  /**
   * Nettoie les fichiers les moins récemment utilisés si l'espace est insuffisant
   */
  async cleanLRUFiles(targetSize: number): Promise<number> {
    const db = await this.getDB();
    const allFiles = await this.getAllFiles();

    // Trier par date d'accès (plus ancien en premier)
    allFiles.sort((a, b) => a.lastAccessed - b.lastAccessed);

    let deletedSize = 0;
    let deletedCount = 0;

    for (const file of allFiles) {
      if (deletedSize >= targetSize) {
        break;
      }
      await this.deleteFile(file.fileId);
      deletedSize += file.fileSize;
      deletedCount++;
    }

    return deletedCount;
  }
}

class OfflineFileService {
  private dbService: IndexedDBService;
  private activeDownloads: Map<string, AbortController> = new Map();

  constructor() {
    this.dbService = new IndexedDBService();
    this._init();
  }

  private _init(): void {
    // Nettoyage automatique au démarrage
    this.dbService.cleanOldFiles().catch(() => {
      // Ignorer les erreurs de nettoyage silencieusement
    });
  }

  /**
   * Obtient les informations de stockage
   */
  async getStorageInfo(): Promise<StorageInfo> {
    if (!navigator.storage || !navigator.storage.estimate) {
      throw new Error('Storage API non disponible');
    }

    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 0;
    const available = quota - usage;
    const percentage = quota > 0 ? usage / quota : 0;

    return {
      usage,
      quota,
      available,
      percentage
    };
  }

  /**
   * Vérifie si l'espace est suffisant pour télécharger un fichier
   */
  private async checkStorageSpace(fileSize: number): Promise<{
    sufficient: boolean;
    message?: string;
  }> {
    const storageInfo = await this.getStorageInfo();

    // Utiliser le minimum entre notre limite artificielle et le quota réel du navigateur
    const effectiveQuota = Math.min(MAX_STORAGE_SIZE, storageInfo.quota);
    const effectiveAvailable = Math.min(
      effectiveQuota - storageInfo.usage,
      storageInfo.available
    );

    // Vérifier la limite effective (notre limite ou quota navigateur, le plus restrictif)
    if (storageInfo.usage + fileSize > effectiveQuota) {
      return {
        sufficient: false,
        message: `Espace de stockage insuffisant. Limite de ${this.formatBytes(effectiveQuota)} atteinte.`
      };
    }

    // Vérifier l'espace disponible réel
    if (fileSize > effectiveAvailable) {
      const needed = fileSize - effectiveAvailable;
      return {
        sufficient: false,
        message: `Espace insuffisant. ${this.formatBytes(needed)} supplémentaires nécessaires.`
      };
    }

    // Avertir si < 10% d'espace restant
    const remainingPercentage =
      (effectiveAvailable - fileSize) / effectiveQuota;
    if (remainingPercentage < STORAGE_WARNING_THRESHOLD) {
      return {
        sufficient: true,
        message: `Attention : moins de 10% d'espace restant après le téléchargement.`
      };
    }

    return { sufficient: true };
  }

  /**
   * Télécharge un fichier et le sauvegarde dans IndexedDB
   */
  async downloadFile(
    fileId: string,
    fileUrl: string,
    fileName: string,
    fileSize: number,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    // Vérifier si déjà téléchargé
    const isDownloaded = await this.dbService.isFileDownloaded(fileId);
    if (isDownloaded) {
      throw new Error('Fichier déjà téléchargé');
    }

    // Vérifier la taille du fichier
    if (fileSize > MAX_FILE_SIZE) {
      throw new Error(
        `Fichier trop volumineux. Taille maximale : ${this.formatBytes(MAX_FILE_SIZE)}`
      );
    }

    // Vérifier l'espace disponible
    const storageCheck = await this.checkStorageSpace(fileSize);
    if (!storageCheck.sufficient) {
      // Essayer de nettoyer les fichiers LRU
      const storageInfo = await this.getStorageInfo();
      const effectiveQuota = Math.min(MAX_STORAGE_SIZE, storageInfo.quota);
      const effectiveAvailable = Math.min(
        effectiveQuota - storageInfo.usage,
        storageInfo.available
      );
      const needed = fileSize - effectiveAvailable;
      if (needed > 0) {
        await this.dbService.cleanLRUFiles(needed);
        // Vérifier à nouveau
        const recheck = await this.checkStorageSpace(fileSize);
        if (!recheck.sufficient) {
          throw new Error(storageCheck.message || 'Espace insuffisant');
        }
      } else {
        throw new Error(storageCheck.message || 'Espace insuffisant');
      }
    }

    // Créer un AbortController pour le téléchargement
    const abortController = new AbortController();
    this.activeDownloads.set(fileId, abortController);

    try {
      // Télécharger le fichier avec timeout
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, DOWNLOAD_TIMEOUT);

      const response = await fetch(fileUrl, {
        signal: abortController.signal,
        credentials: 'include' // Inclure les cookies pour l'authentification
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      // Obtenir le Content-Length si disponible
      const contentLength = response.headers.get('content-length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : fileSize;

      // Lire le fichier avec suivi de progression
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Impossible de lire le fichier');
      }

      const chunks: Uint8Array[] = [];
      let receivedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        receivedBytes += value.length;

        if (onProgress && totalBytes > 0) {
          const progress = (receivedBytes / totalBytes) * 100;
          onProgress(Math.min(progress, 100));
        }
      }

      // Créer le Blob
      const blob = new Blob(chunks as BlobPart[], {
        type: response.headers.get('content-type') || 'application/octet-stream'
      });

      // Sauvegarder dans IndexedDB
      await this.dbService.saveFile(fileId, blob, {
        fileName,
        fileSize: blob.size,
        mimeType:
          response.headers.get('content-type') || 'application/octet-stream',
        sourceUrl: fileUrl,
        version: Date.now() // Version basée sur timestamp
      });

      this.activeDownloads.delete(fileId);
    } catch (error: any) {
      this.activeDownloads.delete(fileId);
      if (error.name === 'AbortError') {
        throw new Error('Téléchargement annulé ou timeout');
      }
      throw error;
    }
  }

  /**
   * Annule un téléchargement en cours
   */
  cancelDownload(fileId: string): void {
    const abortController = this.activeDownloads.get(fileId);
    if (abortController) {
      abortController.abort();
      this.activeDownloads.delete(fileId);
    }
  }

  /**
   * Vérifie si un fichier est téléchargé
   */
  async isFileDownloaded(fileId: string): Promise<boolean> {
    return this.dbService.isFileDownloaded(fileId);
  }

  /**
   * Récupère un fichier depuis IndexedDB
   */
  async getFile(fileId: string): Promise<Blob | null> {
    return this.dbService.getFile(fileId);
  }

  /**
   * Récupère les métadonnées d'un fichier
   */
  async getMetadata(fileId: string): Promise<FileMetadata | null> {
    return this.dbService.getMetadata(fileId);
  }

  /**
   * Supprime un fichier de IndexedDB
   */
  async deleteFile(fileId: string): Promise<void> {
    return this.dbService.deleteFile(fileId);
  }

  /**
   * Récupère tous les fichiers téléchargés
   */
  async getAllFiles(): Promise<FileMetadata[]> {
    return this.dbService.getAllFiles();
  }

  /**
   * Nettoie les fichiers anciens
   */
  async cleanOldFiles(): Promise<number> {
    return this.dbService.cleanOldFiles();
  }

  /**
   * Formate les bytes en format lisible
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Crée une URL blob pour un fichier
   */
  createBlobURL(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  /**
   * Révoke une URL blob
   */
  revokeBlobURL(url: string): void {
    URL.revokeObjectURL(url);
  }
}

export const offlineFileService = new OfflineFileService();

// Rendre disponible globalement
(window as any).offlineFileService = offlineFileService;
